// Shared helpers for the "why was this blocked" diagnosis surfaced on blocked
// runs and blocked (never-executed) batch items. The backend stores the block
// reason in different places depending on how the case was blocked, so this is
// the single place that knows them all:
//
//   blocked run    → the exe agent's "done" stop-step carries `detail`; the
//                    extension panel's steps carry `status`/`reason`
//   parked item    → `last_error`, else the frozen case fields
//                    `destructive` / `run_mode` / `manual_reason`
//
// Everything rendered here is derived from those real stored values. If nothing
// is stored, we say so out loud instead of inventing a reason.

function trimText(v) {
  return String(v || "").trim();
}

// The step the runners use to end a run abruptly (upload skipped, destructive
// gate, prerequisite unmet) is an action "done" whose success is false and whose
// detail/reason explains what stopped it.
function reasonFromSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  for (const s of list) {
    const txt = trimText(s.detail || s.reason || s.text);
    const action = String(s.action || "").toLowerCase();
    if (action === "done" && s.success === false && txt) return txt;
  }
  for (const s of list) {
    if (String(s.status || "").toUpperCase() === "BLOCKED") {
      const txt = trimText(s.reason || s.detail || s.text);
      if (txt) return txt;
    }
  }
  for (const s of list) {
    const txt = trimText(s.detail || s.reason || s.text);
    if (txt && /blocked|not executed|could not be executed|skipped/i.test(txt)) return txt;
  }
  return "";
}

// Reason for a run detail payload (any runner).
export function blockedReasonOfRun(d) {
  if (!d) return "";
  const fromSteps = reasonFromSteps(d.steps || d.step_results);
  if (fromSteps) return fromSteps;
  const report = d.bug_report || d.bug;
  return trimText(report && report.reason);
}

// Reason for a batch item row (`list_batch_items` payload). Falls back from the
// recorded error to the frozen case fields that made the case un-runnable in
// the first place — a manual-only or destructive case is parked with no run, so
// there is no agent-generated detail to read.
export function blockedReasonOfItem(i) {
  if (!i) return "";
  const direct = trimText(i.last_error);
  if (direct) return direct;
  const manual = trimText(i.manual_reason);
  const mode = String(i.run_mode || "").toLowerCase();
  if (i.destructive) {
    return "This case is marked destructive — running it would change or delete real data, so it was not executed. Run it manually against a safe environment.";
  }
  if (mode === "manual_only" || manual) {
    return manual || "This case is marked manual-only — it requires a human to run it.";
  }
  return "";
}

// Classify a stored reason into a user-facing diagnosis. Every line is derived
// from the actual reason text; nothing is hardcoded to a specific project.
export function buildBlockedDiagnosis(raw) {
  const reason = trimText(raw);
  const text = reason.toLowerCase();
  const has = (...words) => words.some((w) => text.includes(w));

  let why;
  let action;
  if (!reason) {
    why = "This test case was marked blocked, but no blocking reason was recorded.";
    action = "No action can be recommended until a blocking reason is recorded.";
  } else if (has("destructive", "change or delete real data")) {
    why = "This case was classified as destructive — running it would create, change or delete real data, so it was intentionally never executed.";
    action = "Run it manually against a safe/staging environment if the change is intended.";
  } else if (has("manual", "human")) {
    why = "This case requires a manual interaction that the agent is not allowed to automate.";
    action = "Run the case manually with a human present.";
  } else if (has("upload", "sample file", "file selection", "file upload")) {
    why = "The case requires supplying a file, and the execution environment could not provide one.";
    action = "Add a matching sample file, or run this case manually with the required file.";
  } else if (has("credential", "password", "sign-in", "sign in", "login", "auth")) {
    why = "The authentication or credentials required for this scenario were not available.";
    action = "Provide the required credentials/configuration and execute the test again.";
  } else if (has("prerequisite", "precondition")) {
    why = "A required prerequisite was not satisfied before execution could start.";
    action = "Ensure the prerequisite is in place and execute the test again.";
  } else if (has("test data", "test_data", "data unavailable", "data was not available")) {
    why = "Required test data was unavailable for this scenario.";
    action = "Provide the required test data and execute the test again.";
  } else if (has("reach", "unreachable", "dns", "offline", "network", "navigate", "page")) {
    why = "The application or page could not be reached from the execution environment.";
    action = "Check that the target application is up and reachable, then execute the test again.";
  } else if (has("element", "selector", "not found")) {
    why = "A required element was not available when the case was checked.";
    action = "Confirm the required element exists for the scenario, then execute the test again.";
  } else if (has("browser", "capability", "headless", "device")) {
    why = "A required browser or device capability was unavailable.";
    action = "Use a device/browser that provides the required capability and execute the test again.";
  } else if (has("forbid", "qa-context", "do not run", "unattended", "research")) {
    why = "The project's safety rules forbid this case from running unattended.";
    action = "Run the case manually, or deliberately relax the project guardrail.";
  } else if (has("environment", "infrastructure", "lease", "went offline", "no longer exists", "deleted")) {
    why = "The execution environment prevented the case from running.";
    action = "Restore the required environment/resources and execute the test again.";
  } else {
    why = "The case was not executed, for the reason recorded below.";
    action = "Resolve the blocking condition and execute the test again.";
  }

  return {
    why,
    reason,
    affected: reason
      ? "Execution was stopped before this case could produce a result."
      : "The test never started, so nothing was executed.",
    action,
  };
}